import { Controller } from "@hotwired/stimulus"

/**
 * Games filter controller for category filtering
 * Usage: data-controller="games-filter" on the filter container
 *        data-games-filter-target="button" on filter buttons
 *        data-games-filter-target="card" on game cards
 *        data-games-filter-target="grid" on the grid container
 *        data-games-filter-target="empty" on empty state element
 *        data-category="category-id" on both buttons and cards
 */
export default class extends Controller<HTMLElement> {
  static targets = ["button", "card", "grid", "empty"]

  declare readonly buttonTargets: HTMLButtonElement[]
  declare readonly cardTargets: HTMLElement[]
  declare readonly gridTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly hasEmptyTarget: boolean

  connect() {
    // Check URL params on connect
    const urlParams = new URLSearchParams(window.location.search)
    const category = urlParams.get("category")
    if (category) {
      this.applyFilter(category)
    }
  }

  filter(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    const category = button.dataset.category || "all"

    this.applyFilter(category)
    this.updateUrl(category)
    this.scrollToGrid()
  }

  reset() {
    this.applyFilter("all")
    this.updateUrl("all")
  }

  private applyFilter(category: string) {
    // Update button styles
    this.buttonTargets.forEach(btn => {
      const btnCategory = btn.dataset.category || "all"
      const isActive = btnCategory === category

      if (isActive) {
        btn.classList.remove("btn-ghost", "border", "border-base-300")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary")
        btn.classList.add("btn-ghost", "border", "border-base-300")
      }
    })

    // Filter cards with animation
    let visibleCount = 0
    this.cardTargets.forEach(card => {
      const cardCategory = card.dataset.category
      const show = category === "all" || cardCategory === category

      if (show) {
        visibleCount++
        card.classList.remove("hidden")
        // Add fade in animation
        card.style.opacity = "0"
        card.style.transform = "translateY(10px)"
        requestAnimationFrame(() => {
          card.style.transition = "opacity 200ms ease, transform 200ms ease"
          card.style.opacity = "1"
          card.style.transform = "translateY(0)"
        })
      } else {
        card.classList.add("hidden")
      }
    })

    // Show/hide empty state
    if (this.hasEmptyTarget) {
      if (visibleCount === 0) {
        this.emptyTarget.classList.remove("hidden")
        this.gridTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        this.gridTarget.classList.remove("hidden")
      }
    }
  }

  private updateUrl(category: string) {
    const url = new URL(window.location.href)
    if (category === "all") {
      url.searchParams.delete("category")
    } else {
      url.searchParams.set("category", category)
    }
    history.pushState({}, "", url)
  }

  private scrollToGrid() {
    // Smooth scroll to grid section
    const gridSection = document.getElementById("all-games")
    if (gridSection) {
      const offset = 100 // Account for sticky header
      const elementPosition = gridSection.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      })
    }
  }
}
